/**
 * #1058 — the file caches OTHER than the folder lists, kept in step with
 * realtime `file:*` events and `resync` for the whole signed-in session.
 *
 * #956 / #960 moved the folder-list patching (`fileKeys.list`) out of the views
 * into `FileListCacheSync`, because a subscriber that lives only while its view
 * is mounted drops every event that arrives while you are on another page. The
 * same was true of every cache here: Home's Projects and Recent, the Shared
 * page, search results and breadcrumbs. Their events arrived with nobody
 * listening (or, for search / recent / breadcrumbs, nobody listened at all),
 * and the cache was served stale until its 30 s `staleTime` ran out. After a
 * reconnect that missed events nothing marked them stale.
 *
 * `FileListCacheSync` calls `applyFileEventToDerivedCaches` for every event and
 * `flushDerivedCachesOnResync` on a file resync. The rules:
 *   - a row the event carries is PATCHED in place (a delete taken out, a rename
 *     merged in) so the next visit never shows it the old way;
 *   - what a patch can't decide (a new row that may now match, an order that
 *     changed, a permission the server must re-resolve) is marked stale by KEY
 *     PREFIX — never `fileKeys.all`, whose 30 s cache is load-bearing for
 *     folder navigation (CLAUDE.md, "Caching & realtime contract");
 *   - marking stale is COALESCED (`DerivedStaleBatch`): a burst of saves is one
 *     refetch of what's on screen, not one per save.
 *
 * The view-local states (the workspace explorer and linked-folder trees, the
 * Bin page's sections) are not TanStack caches; each reloads itself on mount
 * and on `resync` while mounted.
 */
import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { FileEvent } from '@papan/realtime/client'
import { binNudgeSharedIn } from '@papan/types'
import type { FileListItem, RecentFileItem } from '@papan/files/client'
import { patchCachedEntries, rowDiffers as differs } from '@papan/files/client/queries'
import { isMiniappFolder, isProjectFolder, isSharedPointer, isSharedPointerRow, pointerKindOf, stripPointerSuffix, type PointerKindRow } from '@papan/types'

type Breadcrumbs = Array<{ id: string; name: string }>
type SearchResult = { files: FileListItem[]; total: number; truncated: boolean }
type RecentResult = { files: RecentFileItem[] }

// The `fileKeys` prefixes, spelled out rather than built from `fileKeys.all` at
// import time: this module loads with every page (through `FileListCacheSync`),
// and evaluating `fileKeys` here broke unrelated suites that partially mock
// `@papan/files/client/queries`. `derived-cache-sync-1058.test.tsx` pins them
// to `fileKeys`.
/** `['files','search', …]` — every search, under every root, query and identity. */
export const SEARCH_PREFIX = ['files', 'search'] as const
/** `['files','recent', …]` — Home / mobile / chat-home "recently modified". */
export const RECENT_PREFIX = ['files', 'recent'] as const
/** #653 — Recent (`fileKeys.recentWork`): grouped by the server, so a change is never patched in.
 *  Both the one-page previews (Home, chat home, phone — one shared read) and the paged Recent page
 *  refetch in {@link RECENT_PAGED_WINDOW_MS} (a Mine read scans the tenant's version history; the
 *  paged one re-reads every page loaded), the paged one never cancelling a page still loading (a
 *  Show more). Only what is ON SCREEN refetches: each surface unsubscribes its query while its deck
 *  is hidden (`subscribed`), so a hidden one is only marked stale and reloads when shown. */
export const RECENT_WORK_PREFIX = ['files', 'recent-work'] as const
export const RECENT_WORK_FIRST_PREFIX = ['files', 'recent-work', 'first'] as const
export const RECENT_WORK_PAGED_PREFIX = ['files', 'recent-work', 'paged'] as const
/** `['files','breadcrumbs', id]`. */
export const BREADCRUMBS_PREFIX = ['files', 'breadcrumbs'] as const
/** `['files','detail', id]`. */
export const DETAIL_PREFIX = ['files', 'detail'] as const
/** `['files','list', parentId, …]` — every folder list. Read here only to find those holding a `.shared` row (#1209 I3);
 *  the lists themselves are `patchListsFromFileEvent`'s. */
export const LIST_PREFIX = ['files', 'list'] as const
/** The Projects page / Home suggestions (`/api/workspaces`, `/api/workspaces/recent`). */
export const PROJECT_LIST_PREFIXES: readonly QueryKey[] = [['projects', 'list'], ['projects', 'recent']]
/** Projects shared IN — read from `shared-overview`, so it moves with the Shared page. */
export const INCOMING_SHARED_PROJECTS_PREFIX = ['projects', 'incoming-shared'] as const
/** The Shared page (`shared-overview`) and its "Show inactive" Bin read (`shared-bin`). */
export const SHARED_PAGE_PREFIXES: readonly QueryKey[] = [['shared-overview'], ['shared-bin'], INCOMING_SHARED_PROJECTS_PREFIX]

/**
 * How a prefix is marked stale.
 *  - `active`: the queries on screen refetch now; the rest are marked stale and
 *    refetch on their next read.
 *  - `none`: only marked stale. Search results — a snapshot of what the person
 *    typed: re-running the search under their cursor on every save elsewhere
 *    would reshuffle the list they are reading. A row the event carries is
 *    still patched in place (D1).
 */
export type StaleMode = 'active' | 'none'

/** The default coalescing window (D2). */
export const STALE_WINDOW_MS = 1000
/**
 * Recently modified gets a longer window (D2). Every file write in the tenant
 * touches it, and on desktop its readers (Home, the chat home) stay mounted
 * behind `display: none`, so they count as on screen: at 1 s, every member's
 * client would re-ask `/api/files/recent` — a permission-checked read —
 * every second of an agent's write burst. A rename or delete the event carries
 * is still patched in at once; only the server's new ORDER waits. (Marking it
 * without refetching and refreshing it when its deck is SHOWN would cost less
 * still — D2 option (b), the owner's call.)
 */
export const RECENT_WINDOW_MS = 5000
/** #653 — Recent: a Mine read scans the tenant's version history and the paged page re-reads every
 *  loaded page, so a longer window than Recently modified's. */
export const RECENT_PAGED_WINDOW_MS = 15000

/**
 * Coalesces "mark these prefixes stale" over a short window. The first request
 * opens the window; everything asked for until it closes is flushed together,
 * once per prefix. A window, not a debounce: a steady stream of saves (an
 * agent writing, an autosave) still flushes every window, never starves. Each
 * window length runs its own timer.
 */
export class DerivedStaleBatch {
  private windows = new Map<number, { pending: Map<string, { key: QueryKey; mode: StaleMode; keepRunning?: boolean }>; timer: ReturnType<typeof setTimeout> | null }>()

  constructor(private readonly qc: QueryClient) {}

  /** `keepRunning`: a fetch already running for the key is left to finish (never cancelled for this
   *  refetch) — for a paged query, where cancelling a "Show more" in flight loses the person's click. */
  mark(key: QueryKey, mode: StaleMode, windowMs = STALE_WINDOW_MS, opts?: { keepRunning?: boolean }): void {
    let w = this.windows.get(windowMs)
    if (!w) { w = { pending: new Map(), timer: null }; this.windows.set(windowMs, w) }
    const id = JSON.stringify(key)
    const prev = w.pending.get(id)
    // `active` wins: one caller wanting the on-screen copy refetched is enough.
    if (!prev || (prev.mode === 'none' && mode === 'active')) w.pending.set(id, { key, mode, ...(opts?.keepRunning ? { keepRunning: true } : {}) })
    if (!w.timer) w.timer = setTimeout(() => this.flushWindow(windowMs), windowMs)
  }

  private flushWindow(windowMs: number): void {
    const w = this.windows.get(windowMs)
    if (!w) return
    if (w.timer) { clearTimeout(w.timer); w.timer = null }
    const batch = [...w.pending.values()]
    w.pending.clear()
    for (const { key, mode, keepRunning } of batch) {
      if (keepRunning) void invalidateKeepingRunning(this.qc, { queryKey: key, refetchType: mode })
      else void this.qc.invalidateQueries({ queryKey: key, refetchType: mode })
    }
  }

  /**
   * The subscriber is going (tenant switched, signed out): apply what's pending
   * as MARKS only, refetching nothing. The marked caches outlive the switch
   * (every key carries its tenant and nothing clears the query cache), so
   * dropping the marks would serve that tenant's stale lists on the way back
   * (review round 3); refetching now would hit the NEXT scope's on-screen
   * queries, or run after sign-out. The marks are by prefix, so they also mark
   * the next tenant's copies stale — one extra refetch on its next read,
   * accepted (code review): narrowing them per identity would mean keying
   * every mark by tenant.
   */
  retire(): void {
    for (const w of this.windows.values()) {
      if (w.timer) { clearTimeout(w.timer); w.timer = null }
      for (const { key } of w.pending.values()) void this.qc.invalidateQueries({ queryKey: key, refetchType: 'none' })
      w.pending.clear()
    }
  }

  /** Drop what's pending without applying it (a resync is about to mark everything anyway). */
  dispose(): void {
    for (const w of this.windows.values()) {
      if (w.timer) { clearTimeout(w.timer); w.timer = null }
      w.pending.clear()
    }
  }
}

/**
 * Patch every cache under `prefix` (or one exact key) through
 * `patchCachedQueries`: an updater that changes nothing writes nothing (TanStack
 * would otherwise stamp the cache fresh), and a cache already marked stale stays
 * stale after a real patch — the patch fixed one row, not what the mark was for.
 */
function patch<T>(qc: QueryClient, entries: Entries, update: (prev: T) => T): void {
  if (entries.length === 0) return
  patchCachedEntries<T>(qc, entries as ReadonlyArray<readonly [QueryKey, T | undefined]>, (prev) => (prev === undefined ? undefined : update(prev)))
}

type Entries = ReadonlyArray<readonly [QueryKey, unknown]>

/**
 * The cached queries each derived cache patches, found in ONE scan of the
 * query cache per event (review round 3): an agent's write burst is hundreds of
 * events a second, and the cache holds a `['files','permission',id]` entry per
 * listed row, so a scan per cache per event added up on every member's client.
 */
interface Buckets { search: Entries; recent: Entries; breadcrumbs: Entries; projects: Entries }
function bucketCaches(qc: QueryClient): Buckets {
  const search: Array<readonly [QueryKey, unknown]> = []
  const recent: Array<readonly [QueryKey, unknown]> = []
  const breadcrumbs: Array<readonly [QueryKey, unknown]> = []
  const projects: Array<readonly [QueryKey, unknown]> = []
  for (const q of qc.getQueryCache().getAll()) {
    const [a, b] = q.queryKey as readonly unknown[]
    if (a === 'files') {
      if (b === 'search') search.push([q.queryKey, q.state.data])
      else if (b === 'recent') recent.push([q.queryKey, q.state.data])
      else if (b === 'breadcrumbs') breadcrumbs.push([q.queryKey, q.state.data])
    } else if (a === 'projects' && (b === 'list' || b === 'recent')) {
      projects.push([q.queryKey, q.state.data])
    }
  }
  return { search, recent, breadcrumbs, projects }
}

/**
 * A rename in place. A drive rename is published as `file:moved` with the same
 * parent (`native-move.ts`); a mount-side source rename as `file:updated`. A
 * #662 follow-move (`previousFileId`) is a different row, never a rename.
 */
function isRenameInPlace(event: FileEvent): boolean {
  if (!event.file) return false
  if (event.type === 'file:updated') return true
  return event.type === 'file:moved' && !event.previousFileId && event.oldParentId !== undefined && event.oldParentId === event.parentId
}

/** A `.shared` / `.chain` row: its listing fields are computed per viewer, so never merged from a broadcast. */
function isPointerRow(event: FileEvent): boolean {
  return pointerKindOf(event.file) !== null
}

/** The ids an event is ABOUT: the row, and for a sync-detected agent `mv` (#662) the old id it replaced. */
function idsOf(event: FileEvent): Set<string> {
  const ids = new Set([event.fileId])
  if (event.previousFileId) ids.add(event.previousFileId)
  return ids
}

/** The row's leaf name: the payload's, else the top-level one every publisher stamps (R15 #112). */
function nameOf(event: FileEvent): string | undefined {
  return event.file?.name ?? event.name
}

/** Fields of a list row a broadcast may carry for every viewer (no per-viewer listing fields). */
function sharedRowFields(event: FileEvent): Partial<FileListItem> | null {
  const f = event.file
  if (!f) return null
  return {
    name: f.name,
    size: f.size,
    mimeType: f.mimeType,
    updatedAt: f.updatedAt,
    // As the drive list patcher: a present null means CLEARED (I3, the publisher
    // sends a loaded null); an absent key means "not sent", so the row keeps its own.
    ...(f.metadataJson !== undefined ? { metadataJson: f.metadataJson } : {}),
  } as Partial<FileListItem>
}

/**
 * The ids an event REMOVES from a cached list, or null: a delete (the row, and
 * — for a #662 follow-move delete — its previous id), or a follow-move's OLD,
 * now-binned id, with or without the new row's payload.
 */
function removedRowIds(event: FileEvent): Set<string> | null {
  if (event.type === 'file:deleted') return idsOf(event)
  if (event.type === 'file:moved' && event.previousFileId && event.previousFileId !== event.fileId) return new Set([event.previousFileId])
  return null
}

/**
 * `rows` without what the event removed: the rows themselves, and for a delete
 * the direct children of a deleted folder (deeper rows can't be told from the
 * event; they go when the cache is next read). The same array when nothing
 * went — `patch` writes nothing then. One rule for search and Recent.
 */
function dropRemovedRows<R extends { id: string; parentId?: string | null }>(rows: R[], event: FileEvent, ids: Set<string>): R[] {
  const childrenToo = event.type === 'file:deleted'
  const kept = rows.filter(r => !ids.has(r.id) && !(childrenToo && r.parentId && ids.has(r.parentId)))
  return kept.length === rows.length ? rows : kept
}

// ─── Search results (`fileKeys.search`) ─────────────────────────────────────

function applyToSearch(qc: QueryClient, event: FileEvent, batch: DerivedStaleBatch, entries: Entries): void {
  const removed = removedRowIds(event)
  // Pointer rows' listing fields are per viewer: never merged from a broadcast.
  const fields = event.type !== 'file:deleted' && !isPointerRow(event) ? sharedRowFields(event) : null
  if (removed || fields) {
    // ONE patch, as Recent: a follow-move drops the old id AND merges a new row
    // already listed (its new name / size) — two patches over one snapshot let
    // the second undo the first. Whether a follow-move's new row now MATCHES is
    // the server's: the stale mark below.
    patch<SearchResult>(qc, entries, (prev) => {
      if (!prev.files) return prev
      const kept = removed ? dropRemovedRows(prev.files, event, removed) : prev.files
      let files = kept
      if (fields) {
        const i = kept.findIndex(f => f.id === event.fileId)
        if (i >= 0 && differs(kept[i], fields)) {
          files = [...kept]
          files[i] = { ...files[i], ...fields }
        }
      }
      return files === prev.files ? prev : { ...prev, files, total: Math.max(0, prev.total - (prev.files.length - files.length)) }
    })
  }
  // Whatever the patch could not decide — a new row that now matches, a
  // rename that stops matching, a move under / out of a search root.
  batch.mark(SEARCH_PREFIX, 'none')
}

// ─── Recently modified (`fileKeys.recent`) ──────────────────────────────────

function applyToRecent(qc: QueryClient, event: FileEvent, batch: DerivedStaleBatch, entries: Entries): void {
  const removed = removedRowIds(event)
  // `updatedBy` is not patched: the event's actor is not always what the
  // server stores as the modifier (share-link writers, COALESCEd columns, live
  // names) — the windowed refetch below brings the real one.
  const f = event.type !== 'file:deleted' && event.file && !isPointerRow(event) ? event.file : null
  const fields = f ? { name: f.name, size: f.size, mimeType: f.mimeType, updatedAt: f.updatedAt } : null
  batch.mark(RECENT_WORK_FIRST_PREFIX, 'active', RECENT_PAGED_WINDOW_MS)
  batch.mark(RECENT_WORK_PAGED_PREFIX, 'active', RECENT_PAGED_WINDOW_MS, { keepRunning: true })
  if (!removed && !f) { batch.mark(RECENT_PREFIX, 'active', RECENT_WINDOW_MS); return }
  // ONE patch: a follow-move both drops the old id and merges the new row, and
  // two patches over one snapshot would let the second put the first's
  // removal back (code review).
  patch<RecentResult>(qc, entries, (prev) => {
    if (!prev.files) return prev
    // The rest of a deleted folder's contents go on the refetch below.
    const kept = removed ? dropRemovedRows(prev.files, event, removed) : prev.files
    let changed = kept !== prev.files
    const files = !f || !fields ? kept : kept.map(r => {
      if (r.id === f.id && differs(r, fields)) {
        changed = true
        return { ...r, ...fields }
      }
      // A renamed folder is the "location" hint of the rows directly in it.
      if (f.isFolder && r.parentId === f.id && r.parentName !== f.name) {
        changed = true
        return { ...r, parentName: f.name }
      }
      return r
    })
    return changed ? { ...prev, files } : prev
  })
  // Order (newest first), new rows and moved rows' locations are the server's.
  batch.mark(RECENT_PREFIX, 'active', RECENT_WINDOW_MS)
}

// ─── Breadcrumbs (`fileKeys.breadcrumbs`) ───────────────────────────────────

function applyToBreadcrumbs(qc: QueryClient, event: FileEvent, batch: DerivedStaleBatch, entries: Entries): void {
  const ids = idsOf(event)
  for (const [key, data] of entries) {
    const crumbs = data as Breadcrumbs | undefined
    const ownId = key[2]
    const onPath = Array.isArray(crumbs) && crumbs.some(c => ids.has(c.id))
    if (!onPath && !(typeof ownId === 'string' && ids.has(ownId))) continue
    if (isRenameInPlace(event) && Array.isArray(crumbs)) {
      // The path is the same, one name changed. Written only when it did
      // (an unchanged write would clear an earlier stale mark).
      const name = event.file!.name
      if (crumbs.some(c => c.id === event.fileId && c.name !== name)) {
        patch<Breadcrumbs>(qc, [[key, crumbs]], (prev) => prev.map(c => (c.id === event.fileId ? { ...c, name } : c)))
      }
      continue
    }
    // Moved, deleted or restored on the way: the path itself changed. A removed
    // item's own crumbs are only marked stale — refetching them now would 404:
    // a deleted one, and the OLD id of a #662 follow-move (binned; code review).
    // (A resync refetches every crumbs query on screen, a deleted item's
    // included: it answers 404 there, and that view is already showing the
    // item as gone. Accepted — a resync is rare and can't say what was deleted.)
    const removed = event.type === 'file:deleted' || (!!event.previousFileId && event.previousFileId !== event.fileId && ownId === event.previousFileId)
    batch.mark(key, removed ? 'none' : 'active')
  }
}

// ─── Projects (`/api/workspaces*`) ──────────────────────────────────────────

/**
 * Whether an event can change the Projects / Home project lists. Two reasons,
 * both needed (the launcher's rule, `eventTouchesTheGrid`):
 *  - the row looks like a project / `.miniapp` FOLDER (created, restored,
 *    re-iconed, deleted) — by name. The firehose of in-project FILE writes is
 *    not one (`isFolder === false`), so a keystroke never refetches the list;
 *  - the row IS a listed project, by id — a project renamed off `.project`
 *    carries its new name, which no longer looks like one.
 */
export function eventTouchesProjects(event: FileEvent, projectLists: Entries): boolean {
  const name = nameOf(event)
  // `file` is absent on legacy publishers; assume a folder then so we don't miss it.
  const isFolder = event.file ? event.file.isFolder : true
  // A FILE row is never a listed project: the in-project write firehose stops here.
  if (!isFolder) return false
  if (name && (isProjectFolder(name) || isMiniappFolder(name))) return true
  const ids = idsOf(event)
  for (const [, rows] of projectLists) {
    if (Array.isArray(rows) && rows.some((p: { folderId: string }) => ids.has(p.folderId))) return true
  }
  return false
}

/**
 * #1141 I1: a FOLDER moved (a drive rename arrives as a move), restored or
 * deleted may hold listed projects BELOW it, and its event names only itself —
 * `eventTouchesProjects` can't see those. No delete publisher carries the row,
 * so a delete of unknown kind counts too. These only MIGHT change a list, so the
 * caller marks them through the slow window (`RECENT_WINDOW_MS`): a delete burst
 * refetches an on-screen list at most once per window.
 */
export function eventMayHoldProjects(event: FileEvent): boolean {
  if (event.type !== 'file:moved' && event.type !== 'file:restored' && event.type !== 'file:deleted') return false
  return !event.file || event.file.isFolder
}

// ─── The Shared page (`shared-overview`, `shared-bin`) ──────────────────────

/**
 * A `.shared` mount appearing / disappearing in this tenant rides the `file:*`
 * channel — mount rows are ordinary file rows. Over-includes on purpose (a
 * refresh trigger): the suffix OR the kind.
 */
export function eventTouchesSharedPage(event: FileEvent): boolean {
  const name = nameOf(event)
  return (!!name && isSharedPointer(name)) || pointerKindOf(event.file) === 'shared'
}

/** Grant changes (a new outbound share, a revoke — incl. the cross-tenant fan-out) ride `org:access_changed`. */
export function markSharedPageStale(batch: DerivedStaleBatch): void {
  for (const prefix of SHARED_PAGE_PREFIXES) batch.mark(prefix, 'active')
}

// ─── Entry points ───────────────────────────────────────────────────────────

/** Apply one `file:*` event to every derived cache. Never touches `fileKeys.list` (that is `patchListsFromFileEvent`'s). */
export function applyFileEventToDerivedCaches(qc: QueryClient, event: FileEvent, batch: DerivedStaleBatch): void {
  const caches = bucketCaches(qc)
  applyToSearch(qc, event, batch, caches.search)
  applyToRecent(qc, event, batch, caches.recent)
  applyToBreadcrumbs(qc, event, batch, caches.breadcrumbs)
  if (eventTouchesProjects(event, caches.projects)) for (const prefix of PROJECT_LIST_PREFIXES) batch.mark(prefix, 'active')
  else if (eventMayHoldProjects(event)) for (const prefix of PROJECT_LIST_PREFIXES) batch.mark(prefix, 'active', RECENT_WINDOW_MS)
  if (eventTouchesSharedPage(event)) markSharedPageStale(batch)
}

/**
 * A Bin-only nudge (`nudge.scope === 'bin'`): a source org deleted or restored
 * something under a share it granted this drive. Its id is the SOURCE tenant's,
 * so it patches nothing here (and `FileListCacheSync` skips the rest) — but it
 * is the one signal that a shared-in item's `broken` state may have flipped, on
 * the Shared page and in the Projects page's "shared in" section (review round
 * 3; the launcher matches the same nudges by name). Coalesced like any mark.
 * A nameless nudge (some delete publishers send none) says nothing either way.
 */
export function applyBinNudgeToDerivedCaches(qc: QueryClient, event: FileEvent, batch: DerivedStaleBatch): void {
  // #1209: the server says whether the subtree held a share to THIS drive — exact for an ancestor of the shared
  // folder and for a share renamed since, which no name can match. Trusted whenever present, either way.
  const sharedIn = binNudgeSharedIn(event)
  if (sharedIn !== undefined) {
    if (sharedIn) markSharedPageStale(batch)
    return
  }
  // No tag — a server older than #1209 (a rolling deploy), or one that couldn't tell (its lookup failed, or the
  // delete left no Bin row): the name match below.
  const name = nameOf(event)
  if (!name) return
  // Nudges fan out on EVERY delete / restore anywhere in a source org that
  // grants this drive anything (review round 3), and `shared-overview` is a
  // cross-tenant tree walk: mark only when the name is one of the shared-in
  // items this client has listed — the launcher's rule (`eventTouchesTheGrid`).
  // A name collision costs one extra refetch. This fallback misses a delete /
  // restore of an ANCESTOR of the shared folder and a root renamed since — the
  // tag above covers both.
  if (sharedInNames(qc).has(name.toLowerCase())) markSharedPageStale(batch)
}

/**
 * #1209 I3: a nudge TAGGED `sharedIn: true` — a share root of this drive was binned or restored, an ANCESTOR of the
 * shared folder included — can flip the greyed state of this drive's own `.shared` row, wherever it sits. No other
 * signal reaches those lists: `fanOutToSharedPointers` finds only pointers aimed at the binned id itself, and the
 * mounted-recipient fan-out runs after the grants are gone. So every cached folder list holding a `.shared` row is
 * marked (the on-screen ones refetch, coalesced like any mark); lists without one are left alone. The open folder's
 * own accumulator is `useFiles`' (it reloads on the same tag). A `false` or absent tag marks nothing here: the
 * pointer fan-out still covers a bin of the shared folder itself. Not covered: a `.chain` into a received share
 * (#847), which greys on its next read.
 */
export function markSharedRowListsStale(qc: QueryClient, batch: DerivedStaleBatch): void {
  for (const q of qc.getQueryCache().findAll({ queryKey: LIST_PREFIX })) {
    const rows = (q.state.data as { files?: PointerKindRow[] } | undefined)?.files
    if (Array.isArray(rows) && rows.some((r) => isSharedPointerRow(r))) batch.mark(q.queryKey, 'active')
  }
}

/** The names (pointer suffix stripped, lower-cased) of the shared-in items in any cached Shared page or Projects list. */
function sharedInNames(qc: QueryClient): Set<string> {
  const names = new Set<string>()
  const add = (n: unknown) => { if (typeof n === 'string') names.add(stripPointerSuffix(n).toLowerCase()) }
  for (const [, data] of qc.getQueriesData<{ incoming?: Array<{ name?: string }> }>({ queryKey: ['shared-overview'] })) {
    for (const row of data?.incoming ?? []) add(row.name)
  }
  for (const [, rows] of qc.getQueriesData<Array<{ name?: string }>>({ queryKey: INCOMING_SHARED_PROJECTS_PREFIX })) {
    if (Array.isArray(rows)) for (const row of rows) add(row.name)
  }
  return names
}

/**
 * A file resync: events were published while this client's socket was gone, so
 * any of these caches may hold rows that changed or no longer exist. Mark them
 * all stale now (no window — a reconnect is rare): what's on screen refetches,
 * the rest on its next read. Search included — the person is not mid-read of a
 * result list the server has since changed under them; it already has.
 */
/** #1264 — an action on a listed row was refused (it may be gone): the lists that can show the row
 *  outside the drive's folders — search, recent, the project lists, the Shared page — are re-read (on
 *  screen now, elsewhere on their next visit). Never the row's own detail. Pending marks are kept. */
export function refreshDerivedCachesForRefusedRow(qc: QueryClient, fileIds: readonly string[]): void {
  if (fileIds.length === 0) return
  // only the caches that actually show one of the rows (a viewer opened on an unrelated file refreshes
  // nothing) — each cache serialised once per batch
  const needles = fileIds.map((id) => `"${id}"`)
  for (const queryKey of [SEARCH_PREFIX, RECENT_PREFIX, RECENT_WORK_PREFIX, ...PROJECT_LIST_PREFIXES, ...SHARED_PAGE_PREFIXES]) {
    const holds = qc.getQueriesData({ queryKey }).some(([, data]) => {
      if (data === undefined) return false
      const text = JSON.stringify(data)
      return needles.some((n) => text.includes(n))
    })
    if (!holds) continue
    if (keepsRunningFetch(queryKey)) void invalidateKeepingRunning(qc, { queryKey })
    else void qc.invalidateQueries({ queryKey })
  }
}

export function flushDerivedCachesOnResync(qc: QueryClient, batch: DerivedStaleBatch): void {
  // Anything still pending is covered by the flush below.
  batch.dispose()
  const prefixes: QueryKey[] = [SEARCH_PREFIX, RECENT_PREFIX, RECENT_WORK_PREFIX, BREADCRUMBS_PREFIX, DETAIL_PREFIX, ...PROJECT_LIST_PREFIXES, ...SHARED_PAGE_PREFIXES]
  for (const queryKey of prefixes) {
    if (keepsRunningFetch(queryKey)) void invalidateKeepingRunning(qc, { queryKey })
    else void qc.invalidateQueries({ queryKey })
  }
}

/** #653 — Recent's paged query: a flush never cancels a page still loading (a Show more). */
function keepsRunningFetch(queryKey: QueryKey): boolean {
  return queryKey === RECENT_WORK_PREFIX
}

/**
 * Invalidate WITHOUT cancelling a fetch already running — and then make sure the refresh still happens.
 * `cancelRefetch: false` hands back the running fetch, whose success clears the invalidation and stamps
 * the data fresh, so the refresh asked for would otherwise be lost (a Show more loading when a resync
 * landed: the earlier pages kept rows the missed events changed). If anything was fetching, invalidate
 * once more after it settles.
 */
export async function invalidateKeepingRunning(qc: QueryClient, filters: { queryKey: QueryKey; refetchType?: StaleMode }): Promise<void> {
  const cache = qc.getQueryCache()
  // The queries fetching NOW (a paused, off-screen one included): only these need a second round.
  const running = cache.findAll({ queryKey: filters.queryKey, fetchStatus: 'fetching' })
  await qc.invalidateQueries(filters, { cancelRefetch: false }).catch(() => { /* the query's own error */ })
  if (running.length === 0) return
  // Wait for THOSE fetches to settle by themselves — a paused query is not refetched by the round
  // above, so its promise is not what that round waited on.
  await Promise.all(running.map((q) => new Promise<void>((resolve) => {
    if (q.state.fetchStatus !== 'fetching') return resolve()
    const off = cache.subscribe((e) => {
      if (e.query === q && q.state.fetchStatus !== 'fetching') { off(); resolve() }
    })
  })))
  const again = new Set(running)
  await qc.invalidateQueries({ ...filters, predicate: (q) => again.has(q) }, { cancelRefetch: false }).catch(() => {})
}
